import { useState } from 'react';

import { RefreshCw } from 'lucide-react';

import { FormSelect } from '@/components/form';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { useToast } from '@/hooks/use-toast';
import { useConfigurations } from '@/hooks/useConfigurations';
import { usePageTitle } from '@/hooks/usePageTitle';
import {
  useAssignInboundTelnyxNumber,
  useSyncTelnyxNumbers,
  useTelnyxNumbers,
} from '@/hooks/useTelnyxNumbers';
import { extractErrorMessage } from '@/lib/utils';

export const TelnyxNumbersPage = () => {
  usePageTitle('Telnyx Numbers');
  const { toast } = useToast();
  const { data: numbers = [], isLoading } = useTelnyxNumbers();
  const { data: configurations = [] } = useConfigurations();

  const syncMutation = useSyncTelnyxNumbers();
  const assignMutation = useAssignInboundTelnyxNumber();

  const [assignConfigByNumberId, setAssignConfigByNumberId] = useState<Record<string, string>>({});

  const handleSync = async () => {
    try {
      const result = await syncMutation.mutateAsync();
      toast({
        variant: 'success',
        title: 'Numbers synced',
        description: `${result.count} numbers synced from Telnyx.`,
      });
    } catch (error) {
      toast({
        variant: 'destructive',
        title: 'Sync failed',
        description: extractErrorMessage(error),
      });
    }
  };

  const handleAssignInbound = async (numberId: string) => {
    try {
      const configurationId = assignConfigByNumberId[numberId] || null;
      await assignMutation.mutateAsync({
        numberId,
        payload: { configuration_id: configurationId },
      });
      toast({
        variant: 'success',
        title: 'Inbound assignment updated',
        description: configurationId
          ? 'Number is now mapped to the selected configuration.'
          : 'Inbound assignment cleared.',
      });
    } catch (error) {
      toast({
        variant: 'destructive',
        title: 'Assignment failed',
        description: extractErrorMessage(error),
      });
    }
  };

  return (
    <div className="space-y-8">
      <header className="flex items-start justify-between">
        <div className="space-y-2">
          <p className="text-muted-foreground text-sm tracking-[0.3em] uppercase">Telephony</p>
          <h1 className="text-3xl font-bold tracking-tight">Telnyx Numbers</h1>
          <p className="text-muted-foreground max-w-3xl">
            Sync Telnyx phone numbers from your account and assign inbound routing to
            configurations. Numbers are bought via the Telnyx portal — sync to import them here.
          </p>
        </div>
        <Button onClick={handleSync} disabled={syncMutation.isPending}>
          <RefreshCw className={`h-4 w-4 ${syncMutation.isPending ? 'animate-spin' : ''}`} />
          Sync Numbers
        </Button>
      </header>

      <Card>
        <CardHeader>
          <CardTitle>Inventory</CardTitle>
          <CardDescription>
            Persisted Telnyx numbers from local database with configuration assignments.
          </CardDescription>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="flex min-h-[220px] items-center justify-center">
              <div className="border-primary h-10 w-10 animate-spin rounded-full border-b-2" />
            </div>
          ) : numbers.length === 0 ? (
            <p className="text-muted-foreground text-sm">
              No Telnyx numbers yet. Click Sync to import from your Telnyx account.
            </p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full border-collapse text-sm">
                <thead>
                  <tr className="text-muted-foreground text-left">
                    <th className="border-b px-3 py-2">Name</th>
                    <th className="border-b px-3 py-2">Number</th>
                    <th className="border-b px-3 py-2">Status</th>
                    <th className="border-b px-3 py-2">Inbound Config</th>
                    <th className="border-b px-3 py-2 text-right">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {numbers.map((number) => (
                    <tr key={number.id}>
                      <td className="border-b px-3 py-2">{number.friendly_name || '-'}</td>
                      <td className="border-b px-3 py-2 font-mono">{number.phone_number}</td>
                      <td className="border-b px-3 py-2">
                        {number.is_active ? 'Active' : 'Inactive'}
                      </td>
                      <td className="border-b px-3 py-2">
                        <FormSelect
                          value={
                            assignConfigByNumberId[number.id] ??
                            number.inbound_configuration_id ??
                            ''
                          }
                          onChange={(event) =>
                            setAssignConfigByNumberId((prev) => ({
                              ...prev,
                              [number.id]: event.target.value,
                            }))
                          }
                        >
                          <option value="">Unassigned</option>
                          {configurations.map((config) => (
                            <option key={config.id} value={config.id}>
                              {config.name}
                            </option>
                          ))}
                        </FormSelect>
                      </td>
                      <td className="border-b px-3 py-2 text-right">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleAssignInbound(number.id)}
                          disabled={assignMutation.isPending}
                        >
                          Save
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};
